/**
 * Debug Helper Utilities
 * 
 * This file contains utility functions to help diagnose rendering and data issues
 * in the Factory App. These are development-only tools.
 */

// Helper to debug dataGrid component issues
export const debugDataGrid = (rows: any[], columns: any[]) => {
  console.group('DataGrid Debug Info');
  console.log(`Rows count: ${rows.length}`);
  console.log(`Columns count: ${columns.length}`);
  
  // Check if rows have ID
  if (rows.length > 0) {
    const firstRow = rows[0];
    console.log('First row keys:', Object.keys(firstRow));
    
    if (firstRow.id === undefined) {
      console.error('ERROR: Rows are missing ID field which is required by DataGrid');
    } else {
      console.log('ID field present - OK');
    }
    
    // Check column field mappings
    const missingFields = columns
      .filter(col => col.field !== 'actions') // Skip action columns
      .filter(col => firstRow[col.field] === undefined)
      .map(col => col.field);
    
    if (missingFields.length > 0) {
      console.error(`ERROR: Row is missing fields defined in columns: ${missingFields.join(', ')}`);
    } else {
      console.log('All column fields exist in data - OK');
    }
  }
  
  // Check columns format
  const invalidColumns = columns.filter(col => !col.field);
  if (invalidColumns.length > 0) {
    console.error(`ERROR: ${invalidColumns.length} columns missing 'field' property`);
  }
  
  console.groupEnd();
};

// Helper to diagnose component rendering issues
export const debugComponentRendering = (componentName: string) => {
  const startTime = performance.now();
  
  return {
    // Call at the start of render
    start: () => {
      console.log(`[RENDER] ${componentName} starting render`);
    },
    
    // Call before return in the component
    end: () => {
      const endTime = performance.now();
      console.log(`[RENDER] ${componentName} completed render in ${(endTime - startTime).toFixed(2)}ms`);
    },
    
    // Call in effect or update handlers
    log: (message: string) => {
      console.log(`[RENDER] ${componentName}: ${message}`);
    }
  };
};

// Helper to diagnose API issues
export const debugApiCall = async <T>(
  apiCall: () => Promise<T>,
  description: string
): Promise<T> => {
  console.log(`[API] Starting ${description}`);
  const startTime = performance.now();
  
  try {
    const result = await apiCall();
    const endTime = performance.now();
    console.log(`[API] ${description} succeeded in ${(endTime - startTime).toFixed(2)}ms`);
    
    if (Array.isArray(result)) {
      console.log(`[API] Returned array of ${result.length} items`);
      if (result.length > 0) {
        console.log('[API] First item sample:', result[0]);
      }
    } else if (result && typeof result === 'object') {
      console.log('[API] Returned object with keys:', Object.keys(result));
    }
    
    return result;
  } catch (error) {
    const endTime = performance.now();
    console.error(`[API] ${description} failed after ${(endTime - startTime).toFixed(2)}ms:`, error);
    throw error;
  }
}; 